import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { recordResponses, updatePatient } from '../reducers/graph';
import {
  patientResponseIds,
  initialQuestionIds,
  responseIds,
} from '@aldebaran/common/constants/kb';
import { Patient } from '@aldebaran/prisma';
import { RootState } from '../types/graph';
import { isValidDate } from '@aldebaran/common/util/date';
import { useInjectConsultation } from './useInjectConsultation';
import { useInjectProfession } from './useInjectProfession';

export const useInjectPatient = ({
  required = true,
}: { required?: boolean } = {}) => {
  const dispatch = useDispatch();
  const injectConsultation = useInjectConsultation({ required });
  const injectProfession = useInjectProfession();

  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );

  const date = useSelector((state: RootState) =>
    state.consultation.date ? new Date(state.consultation.date) : undefined,
  );
  const noAppointment = useSelector(
    (state: RootState) => state.consultation.noAppointment,
  );

  const responses = useSelector((state: RootState) => state.kb.responses);

  return useCallback(
    (patient: Patient) => {
      dispatch(updatePatient(patient));

      if (!patientResponseIds.every((id) => responses[id])) {
        if (required) {
          throw new Error('Missing patient responses');
        }
        // KB responses not loaded yet (e.g. during early invitation auth flow).
        // Skip pre-filling — DOB/SEX get recorded again when the
        // patient-information page injects them, and INTERLOCUTER is
        // centralised in that page (see PatientInformation.tsx).
      } else {
        const dateOfBirth = patient.dateOfBirth
          ? new Date(patient.dateOfBirth)
          : undefined;

        if (isValidDate(dateOfBirth)) {
          dispatch(
            recordResponses({
              questionId: initialQuestionIds.DATE_OF_BIRTH,
              currentPage,
              responses: [
                {
                  ...responses[responseIds.DATE_OF_BIRTH],
                  value: { value: dateOfBirth.getTime() },
                },
              ],
            }),
          );
        }

        if (patient.sex === 'MALE' || patient.sex === 'FEMALE') {
          dispatch(
            recordResponses({
              questionId: initialQuestionIds.SEX,
              currentPage,
              responses: [
                {
                  ...responses[
                    patient.sex === 'MALE'
                      ? responseIds.SEX.MALE
                      : responseIds.SEX.FEMALE
                  ],
                  value: { value: true },
                },
                {
                  ...responses[
                    patient.sex === 'MALE'
                      ? responseIds.SEX.FEMALE
                      : responseIds.SEX.MALE
                  ],
                  value: { value: false },
                },
              ],
            }),
          );
        }
      }

      if (patient.profession) injectProfession(patient.profession);

      if (date) {
        injectConsultation({ date });
      } else if (noAppointment) {
        injectConsultation({ date: new Date() });
      }
    },
    [dispatch, currentPage, responses, required],
  );
};
